import { useCallback, useState } from 'react';

/**
 * De kostenteller van één consult.
 *
 * Deze hook rekent niets meer uit, en dat is het hele punt. De vorige versie las
 * `billing_settings` rechtstreeks uit de database -- `voice_selling_price_per_second`
 * en `llm_margin_percentage` -- en paste die tarieven zelf toe. De marge van iSOFT
 * stond dus in elke browser die een consult opende, en een gemanipuleerde client
 * kon de teller op elke gewenste waarde zetten.
 *
 * Nu geeft elke route die kosten maakt het bedrag terug: `/api/ai/*` in het
 * JSON-antwoord, `/api/speech/tts` in de header `X-Kosten`, en `/api/usage` voor de
 * opnameduur. Wat hier gebeurt is optellen.
 *
 * De categorieën blijven zoals ze waren, want het scherm dat ze toont is niet
 * veranderd.
 */

export interface ConsultationCosts {
  recording: number;
  translation: number;
  summarization: number;
  icpc: number;
  total: number;
}

export type KostenCategorie = keyof Omit<ConsultationCosts, 'total'>;

const LEEG: ConsultationCosts = {
  recording: 0,
  translation: 0,
  summarization: 0,
  icpc: 0,
  total: 0,
};

export interface ConsultationCostTracker {
  costs: ConsultationCosts;
  /** Telt een bedrag op dat van de server komt. Negatieve waarden worden genegeerd. */
  voegToe: (categorie: KostenCategorie, bedrag: number) => void;
  reset: () => void;
}

/**
 * `actief` bepaalt of de teller iets bijhoudt. Zonder sessie is er geen consult, en
 * dan hoort de teller op nul te blijven staan in plaats van bedragen te tonen die
 * bij een vorige praktijk horen.
 */
export function useConsultationCosts(actief = true): ConsultationCostTracker {
  const [costs, setCosts] = useState<ConsultationCosts>(LEEG);

  const voegToe = useCallback(
    (categorie: KostenCategorie, bedrag: number) => {
      if (!actief) return;
      // Een niet-eindig of negatief bedrag komt niet uit een geldige berekening; het
      // optellen zou de teller alleen onbetrouwbaar maken.
      if (!Number.isFinite(bedrag) || bedrag <= 0) return;

      setCosts((vorige) => ({
        ...vorige,
        [categorie]: vorige[categorie] + bedrag,
        total: vorige.total + bedrag,
      }));
    },
    [actief],
  );

  const reset = useCallback(() => setCosts(LEEG), []);

  return { costs, voegToe, reset };
}
